/**
 * MessageDetail — provenance for one message, the third pane of the Mobile
 * Forensics view.
 *
 * The thread renders a message the way the device showed it; this answers the
 * question that view cannot: where did this row actually come from. The
 * heading of the panel is therefore the source artifact — the
 * `<domain>/<relative path>` key the sealed bundle stores the file under, which
 * is also the key `chain_of_custody.json` records its SHA-256 against, so an
 * examiner can walk one message back to a hashed file.
 *
 * Everything is verbatim and monospaced: identifiers and timestamps are shown
 * as recorded rather than prettified, because they are evidence. `sentAt` is
 * the one field shown twice — once localised for reading, once as the raw
 * instant.
 */

import { XIcon } from 'lucide-react'
import type { FC, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import type { CommsMessage } from '@/services/api/comms'
import { appLabel, attachmentName, DIRECTION_OUT } from './comms-format'

interface MessageDetailProps {
  message: CommsMessage
  onClose: () => void
}

const Field: FC<{ label: string; children: ReactNode }> = ({ label, children }) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-2xs font-medium uppercase tracking-wide text-muted-foreground">
      {label}
    </span>
    <span className="break-words font-mono text-xs text-foreground">{children}</span>
  </div>
)

/** The artifact path, split so the domain does not crowd out the file name. */
const SourceArtifact: FC<{ source: string }> = ({ source }) => {
  const cut = source.indexOf('/')
  const domain = cut === -1 ? null : source.slice(0, cut)
  const path = cut === -1 ? source : source.slice(cut + 1)
  return (
    <div className="rounded-md border border-border bg-muted/40 px-2.5 py-2">
      {domain && <p className="break-all font-mono text-2xs text-muted-foreground">{domain}/</p>}
      <p className="break-all font-mono text-xs text-foreground">{path}</p>
    </div>
  )
}

export const MessageDetail: FC<MessageDetailProps> = ({ message, onClose }) => {
  const { t, i18n } = useTranslation()
  const outgoing = message.direction === DIRECTION_OUT

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2.5">
        <h3 className="truncate text-sm font-semibold text-foreground">
          {t('comms.detail.title')}
        </h3>
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          aria-label={t('comms.detail.close')}
          className="size-9 shrink-0 md:size-7"
        >
          <XIcon className="size-4" />
        </Button>
      </div>

      <div className="min-h-0 flex-1 space-y-3.5 overflow-y-auto px-3 py-3">
        <div className="flex flex-col gap-1">
          <span className="text-2xs font-medium uppercase tracking-wide text-muted-foreground">
            {t('comms.detail.source')}
          </span>
          {message.source ? (
            <SourceArtifact source={message.source} />
          ) : (
            <p className="rounded-md border border-dashed border-border px-2.5 py-2 text-xs text-muted-foreground">
              {t('comms.detail.sourceUnknown')}
            </p>
          )}
        </div>

        <div className="flex flex-wrap gap-1.5">
          <Badge variant="outline" className="text-2xs">
            {appLabel(message.app, t)}
          </Badge>
          <Badge variant="secondary" className="text-2xs">
            {t(outgoing ? 'comms.detail.outgoing' : 'comms.detail.incoming')}
          </Badge>
          <Badge variant="secondary" className="font-mono text-2xs">
            {message.msgType}
          </Badge>
        </div>

        <Field label={t('comms.detail.messageId')}>{message.messageId}</Field>

        <Field label={t('comms.detail.sentAt')}>
          {message.sentAt ? (
            <>
              {new Date(message.sentAt).toLocaleString(i18n.language)}
              <span className="mt-0.5 block text-2xs text-muted-foreground">{message.sentAt}</span>
            </>
          ) : (
            <span className="text-muted-foreground">{t('comms.detail.none')}</span>
          )}
        </Field>

        <Field label={t('comms.detail.sender')}>
          {message.sender ?? (
            <span className="text-muted-foreground">{t('comms.detail.none')}</span>
          )}
        </Field>
        <Field label={t('comms.detail.recipient')}>
          {message.recipient ?? (
            <span className="text-muted-foreground">{t('comms.detail.none')}</span>
          )}
        </Field>

        {message.replyTo && <Field label={t('comms.detail.replyTo')}>{message.replyTo}</Field>}

        {message.attachmentRef && (
          <Field label={t('comms.detail.attachment')}>
            {attachmentName(message.attachmentRef)}
            <span className="mt-0.5 block break-all text-2xs text-muted-foreground">
              {message.attachmentRef}
            </span>
          </Field>
        )}

        <Field label={t('comms.detail.origin')}>{message.origin}</Field>
      </div>
    </div>
  )
}
